import { ShimmerText } from '@/components/feedback/shimmer-text';
import { ThinkingIcon } from '@/features/copilot/components/thinking-icon';
import { useAnalytics } from '@/services/analytics';
import { shuffleArray } from '@/utils/shuffle';
import { isEmptyString } from '@/utils/string';
import {
  Box,
  FormattedRelativeTime,
  PlainButton,
  SkeletonShape,
  SkeletonText,
  Text,
  Truncated,
} from '@customerio/pluma-components/react';
import { AnimatePresence, m, useReducedMotion } from 'motion/react';
import { useEffect, useMemo, useState } from 'react';
import { useCopilotHistory } from '../../hooks';
import * as styles from '../../index.css';
import { useCopilotSessionContext } from '../session-provider';

const CYCLING_STATUS_WORDS = [
  'Thinking…',
  'Working…',
  'Analyzing…',
  'Researching…',
  'Considering…',
  'Processing…',
  'Exploring…',
  'Reasoning…',
  'Evaluating…',
  'Pondering…',
  'Cooking…',
  'Brewing something up…',
  'Scribbling on a napkin…',
  'Connecting dots…',
  'Doing some napkin calculations…',
  'Questioning everything…',
  'Becoming sentient (just kidding)…',
];

export function CyclingStatusText() {
  const shouldReduceMotion = useReducedMotion();
  const [words] = useState(() => shuffleArray(CYCLING_STATUS_WORDS));
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (shouldReduceMotion === true) return;
    const interval = setInterval(() => {
      setIndex((previous) => (previous + 1) % words.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [shouldReduceMotion, words.length]);

  if (shouldReduceMotion === true) {
    return (
      <Box display="flex" alignItems="center" gap="050">
        <ThinkingIcon />
        <ShimmerText variant="gradient" size="s">
          Working…
        </ShimmerText>
      </Box>
    );
  }

  return (
    <Box display="flex" alignItems="center" gap="050">
      <ThinkingIcon />
      <Box position="relative" style={{ overflow: 'hidden' }}>
        <AnimatePresence mode="wait">
          <m.div
            key={words[index]}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.25 }}
          >
            <ShimmerText variant="gradient" size="s">
              {words[index]}
            </ShimmerText>
          </m.div>
        </AnimatePresence>
      </Box>
    </Box>
  );
}

export function RecentChats() {
  const session = useCopilotSessionContext();
  const history = useCopilotHistory();
  const { track } = useAnalytics();

  const recentSessions = useMemo(() => {
    if (history.data == null) return [];
    return history.data.pages.flatMap((page) => page.sessions).slice(0, 3);
  }, [history.data]);

  if (history.isLoading || recentSessions.length === 0) {
    return null;
  }

  function continueSession(id: number) {
    track('aiAssistant', 'historyItemClicked', { 'Click Location': 'empty-state' });
    session.reset().initialize(`${id}`);
  }

  return (
    <Box display="flex" flexDirection="column" gap="050" w="full">
      {recentSessions.map((s) => (
        <PlainButton key={s.id} onClick={() => continueSession(s.id)} className={styles.recentChatItem}>
          <Text size="s" weight="medium" display="block" minW="0">
            <Truncated cursor="pointer">{!isEmptyString(s.name) ? s.name : `Chat ${s.id}`}</Truncated>
          </Text>
          <Text size="xs" color="subtle" flexShrink="0" whiteSpace="nowrap">
            <FormattedRelativeTime date={s.created_at} shouldUppercaseFirst={false} cursor="pointer" />
          </Text>
        </PlainButton>
      ))}
    </Box>
  );
}

export function ChatSkeleton() {
  return (
    <Box display="flex" flexDirection="column" gap="300" py="200" px="100">
      {/* User message skeleton — right-aligned bubble */}
      <Box display="flex" justifyContent="flex-end">
        <SkeletonShape style={{ width: '55%', height: 40, borderRadius: 18 }} />
      </Box>
      {/* Model response skeleton — left-aligned lines */}
      <Box display="flex" flexDirection="column" gap="100">
        <SkeletonText text="product-p" lines={3} />
      </Box>
      {/* Second user message */}
      <Box display="flex" justifyContent="flex-end">
        <SkeletonShape style={{ width: '40%', height: 40, borderRadius: 18 }} />
      </Box>
      {/* Second model response */}
      <Box display="flex" flexDirection="column" gap="100">
        <SkeletonText text="product-p" lines={2} />
      </Box>
    </Box>
  );
}
